/**
 * One live call per account across devices — what this device says and does.
 *
 * Kuwait demo, 2026-09-24: one account signed in on several devices broke a
 * call. The server (vrs-server/lib/call-holders.js) now keeps one device per
 * call and never refuses without saying which device has the call and what
 * can be done. This module is the platform-neutral half on the device side:
 * the copy for every notice, and the small controllers that poll, wait and
 * act. The web room page draws the notices full-screen
 * (accountDevicePresenter.web.ts); native shows them as notifications
 * (accountDevicePresenter.native.ts).
 *
 * Wording rules (product owner, 2026-09-24): a client moving their own call
 * between their own devices is a "move". Interpreters never move a call; a
 * held-out interpreter device can only be used once the other call ends.
 * "Transfer" and "handoff" mean something else in this product and never
 * appear here.
 *
 * Every server string is treated as data: presenters render text, never HTML.
 */
export const DEFAULT_HOLD_RECHECK_MS = 5000;
/** How long "The call stayed here" stays up before it clears itself. */
export const LAPSED_NOTICE_MS = 8000;
const OTHER_DEVICE = 'another device';
function deviceName(device) {
    return device?.trim() || OTHER_DEVICE;
}
function sinceMs(since) {
    const time = since ? Date.parse(since) : NaN;
    return Number.isFinite(time) ? time : undefined;
}
/**
 * Live call length since the server's start time, e.g. "4:05" or "1:02:09".
 *
 * @returns {string|undefined} Undefined when the start time is unknown.
 */
export function formatElapsed(since, now) {
    const start = sinceMs(since);
    if (start === undefined) {
        return undefined;
    }
    const total = Math.max(0, Math.floor((now - start) / 1000));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = String(total % 60).padStart(2, '0');
    return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
}
function formatStartTime(since) {
    const start = sinceMs(since);
    if (start === undefined) {
        return undefined;
    }
    try {
        return new Date(start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    catch {
        return undefined;
    }
}
/** "Checked 3s ago", from the last answer the server gave. */
export function formatCheckedAgo(checkedAt, now) {
    if (checkedAt === undefined) {
        return undefined;
    }
    return `Checked ${Math.max(0, Math.round((now - checkedAt) / 1000))}s ago`;
}
/**
 * The words and choices for a notice at `now`. Pure: presenters call it
 * again every second so elapsed and "checked" times stay live.
 */
export function describeAccountDeviceNotice(notice, now = Date.now()) {
    switch (notice.kind) {
        case 'superseded':
            // Another tab of this same browser took the account's queue socket
            // (opening the portal mid-call does this). The call on this page is
            // unaffected, so a blocking "another device" alarm would be false.
            if (notice.sameBrowser === true) {
                return {
                    blocking: false,
                    title: 'This account opened in another tab',
                    body: ['Your call continues here.'],
                    actions: []
                };
            }
            return {
                blocking: true,
                title: 'This account was opened on another device',
                body: [
                    'This page stopped receiving calls because the same account signed in somewhere else.',
                    'Use this device instead to switch back to it here, or close this page.'
                ],
                actions: [
                    { id: 'useThisDevice', label: 'Use this device instead', primary: true },
                    { id: 'close', label: 'Close' }
                ]
            };
        case 'moving':
            return {
                blocking: false,
                title: `Moving this call to ${deviceName(notice.device)}…`,
                body: ['The call stays here until it connects there.'],
                actions: []
            };
        case 'lapsed':
            return {
                blocking: false,
                title: `The call stayed here — ${deviceName(notice.device)} did not connect.`,
                body: [],
                actions: []
            };
        case 'moved':
            return {
                blocking: true,
                title: `This call moved to ${deviceName(notice.device)}`,
                body: ['The call continues there. It was not hung up.'],
                actions: [
                    { id: 'moveBack', label: 'Move it back here', primary: true },
                    { id: 'home', label: 'Back to home' }
                ]
            };
        case 'held': {
            const device = deviceName(notice.device);
            if (notice.ended) {
                return {
                    blocking: true,
                    title: `The call on ${device} has ended`,
                    body: ['You can use this device now.'],
                    actions: [
                        { id: 'useThisDevice', label: 'Use this device', primary: true },
                        { id: 'home', label: 'Back to home' }
                    ]
                };
            }
            const startedAt = formatStartTime(notice.since);
            const elapsed = formatElapsed(notice.since, now);
            const checked = formatCheckedAgo(notice.checkedAt, now);
            return {
                blocking: true,
                title: 'This account is in a call on another device',
                body: [
                    `${device} is in a call${startedAt ? ` that started at ${startedAt}` : ''}. `
                        + 'You can use this device when that call ends.',
                    ...elapsed ? [`In the call for ${elapsed}`] : [],
                    ...checked ? [checked] : []
                ],
                actions: [{ id: 'home', label: 'Back to home' }]
            };
        }
        case 'callOpenElsewhere': {
            const device = deviceName(notice.device);
            if (notice.phase === 'ended') {
                return {
                    blocking: true,
                    title: 'This call has ended',
                    body: [],
                    actions: [{ id: 'home', label: 'Back to home', primary: true }]
                };
            }
            const elapsed = formatElapsed(notice.since, now);
            const title = `This call is open on ${device}${elapsed ? ` · ${elapsed}` : ''}`;
            if (notice.phase === 'waiting') {
                const checked = formatCheckedAgo(notice.checkedAt, now);
                return {
                    blocking: true,
                    title,
                    body: [
                        `Waiting for ${device} to leave the call. You will join as soon as it does.`,
                        ...checked ? [checked] : [],
                        ...notice.error ? [notice.error] : []
                    ],
                    actions: [
                        { id: 'moveHere', label: 'Move call here', primary: true },
                        { id: 'home', label: 'Back to home' }
                    ]
                };
            }
            return {
                blocking: true,
                title,
                body: ['Move the call to this device, or wait until that device leaves it.'],
                actions: [
                    { id: 'moveHere', label: 'Move call here', primary: true },
                    { id: 'wait', label: 'Wait' }
                ]
            };
        }
    }
}
/** Whether a notice has text that changes with time (elapsed, "checked"). */
export function isLiveNotice(notice) {
    return (notice.kind === 'held' && !notice.ended)
        || (notice.kind === 'callOpenElsewhere' && notice.phase !== 'ended');
}
const systemClock = {
    clearInterval: id => clearInterval(id),
    now: () => Date.now(),
    setInterval: (callback, ms) => setInterval(callback, ms)
};
/**
 * An interpreter session held out by the server: show which device is in the
 * call, re-ask every `recheckAfterMs`, and once that call has ended offer
 * "Use this device". Nothing here re-authenticates on its own — the user
 * decides, because on a shared account the other device may be someone else.
 */
export function watchHeldSession(options) {
    const { dismiss, goHome, present, service } = options;
    const clock = options.clock || systemClock;
    let notice = {
        kind: 'held',
        device: options.hold.device,
        since: options.hold.since,
        ended: false
    };
    let timer;
    let stopped = false;
    const stopPolling = () => {
        if (timer !== undefined) {
            clock.clearInterval(timer);
            timer = undefined;
        }
    };
    const stop = () => {
        stopped = true;
        stopPolling();
        service.off('accountCallStatus', onStatus);
    };
    const handlers = {
        home: () => {
            stop();
            goHome();
        },
        useThisDevice: () => {
            stop();
            dismiss('held');
            service.retakeSession();
        }
    };
    const show = () => present(notice, handlers);
    function onStatus(status) {
        if (stopped || (status.role && status.role !== 'interpreter')) {
            return;
        }
        if (status.held) {
            notice = {
                ...notice,
                device: status.device || notice.device,
                since: status.since || notice.since,
                checkedAt: clock.now()
            };
        }
        else {
            stopPolling();
            notice = { ...notice, ended: true, checkedAt: clock.now() };
        }
        show();
    }
    service.on('accountCallStatus', onStatus);
    timer = clock.setInterval(() => service.requestAccountCallStatus(), Math.max(1000, options.hold.recheckAfterMs || DEFAULT_HOLD_RECHECK_MS));
    show();
    return { stop };
}
/**
 * A client opened a room whose call is open on another device of the account
 * (409 CALL_ACTIVE_ON_OTHER_DEVICE). Offer "Move call here" or "Wait"; while
 * waiting, re-try the room token every 5 s with a visible "Checked Ns ago".
 * If the other device leaves while the call continues, join automatically —
 * the user already asked to join. If the call ends, say so.
 */
export function runCallOpenElsewhereChoice(options) {
    const { attempt, dismiss, onHome, onJoin, onMove, present } = options;
    const clock = options.clock || systemClock;
    let notice = {
        kind: 'callOpenElsewhere',
        device: options.hold.device,
        since: options.hold.since,
        phase: 'choose'
    };
    let timer;
    let inFlight = false;
    let stopped = false;
    const stop = () => {
        stopped = true;
        if (timer !== undefined) {
            clock.clearInterval(timer);
            timer = undefined;
        }
    };
    const leave = (next) => () => {
        stop();
        dismiss('callOpenElsewhere');
        next();
    };
    const handlers = {
        home: leave(onHome),
        moveHere: leave(onMove),
        wait: () => {
            if (stopped || timer !== undefined) {
                return;
            }
            notice = { ...notice, phase: 'waiting' };
            show();
            timer = clock.setInterval(() => {
                void check();
            }, options.retryMs || DEFAULT_HOLD_RECHECK_MS);
        }
    };
    const show = () => present(notice, handlers);
    async function check() {
        if (inFlight || stopped) {
            return;
        }
        inFlight = true;
        let result;
        try {
            result = await attempt();
        }
        catch {
            result = { outcome: 'failed', message: 'Could not reach the server. Still trying.' };
        }
        finally {
            inFlight = false;
        }
        if (stopped) {
            return;
        }
        if (result.outcome === 'joined') {
            leave(onJoin)();
            return;
        }
        if (result.outcome === 'ended') {
            stop();
            notice = { ...notice, phase: 'ended', checkedAt: clock.now(), error: undefined };
            show();
            return;
        }
        notice = {
            ...notice,
            ...result.outcome === 'held' && result.hold
                ? { device: result.hold.device || notice.device, since: result.hold.since || notice.since }
                : {},
            checkedAt: clock.now(),
            error: result.outcome === 'failed' ? result.message : undefined
        };
        show();
    }
    show();
    return { stop };
}
/**
 * Copy for a call-move message from the server. Fields are optional because
 * they come off the wire.
 */
export function moveNotice(kind, data) {
    return kind === 'moved'
        ? { kind, callId: data.callId, device: data.device, roomName: data.roomName }
        : { kind, device: data.device };
}
